import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { useMusicT, useSongIdea } from './kit';
import type { MusicLandingPage } from './types';

/**
 * What this kind of song is and how to ask for it: the page's own text,
 * example prompts that go into the song box, and related tools.
 */
export default function PageDetails({ page }: { page: MusicLandingPage }) {
  const { t } = useMusicT();
  const { fillIdea } = useSongIdea();
  const reveal = useReveal<HTMLDivElement>(0.15);
  const details = page.details;
  if (!details) return null;

  const prompts = page.mode === 'lyrics' ? [] : (details.prompts ?? []);

  return (
    <section className='py-6 sm:py-10'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='100% 100%'
          halftoneReach={45}
          className='px-6 py-14 sm:px-12 sm:py-16 lg:px-16'
        >
          <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
            <div className='lg:col-span-5'>
              <SectionHeading size='md' title={t(details.title)} />
              <p className={clsxm('mt-6 text-[17px] leading-8', tone.body)}>
                {t(details.body)}
              </p>
              {details.links && details.links.length > 0 && (
                <ul className='mt-7 flex flex-wrap gap-3'>
                  {details.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className='inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
                      >
                        {t(link.label)}
                        <ArrowGlyph />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div
              ref={reveal.ref}
              data-reveal={reveal.state}
              className={clsxm(styles.reveal, 'lg:col-span-7')}
            >
              {details.example ? (
                <figure
                  className={clsxm(
                    styles.rv,
                    'rounded-3xl bg-white p-6 shadow-[0_24px_60px_-34px_rgba(40,30,120,0.45)] ring-1 ring-[#0f0f28]/[0.06] sm:p-8 dark:bg-[#14141f] dark:ring-white/10',
                  )}
                >
                  <figcaption
                    className={clsxm(
                      'text-[11px] font-semibold uppercase tracking-[0.14em]',
                      tone.quiet,
                    )}
                  >
                    {t('Example lyrics')}
                  </figcaption>
                  <pre
                    className={clsxm(
                      'mt-4 whitespace-pre-wrap font-sans text-[16px] leading-8',
                      tone.ink,
                    )}
                  >
                    {t(details.example)
                      .split(/(\[[^\]\n]+\])/)
                      .map((part, index) =>
                        /^\[[^\]]+\]$/.test(part) ? (
                          <span
                            key={index}
                            className='font-mono text-sm font-semibold text-primary-600 dark:text-primary-400'
                          >
                            {part}
                          </span>
                        ) : (
                          part
                        ),
                      )}
                  </pre>
                </figure>
              ) : null}

              {prompts.length > 0 && (
                <>
                  <p
                    className={clsxm(
                      'text-[11px] font-semibold uppercase tracking-[0.14em]',
                      tone.quiet,
                    )}
                  >
                    {t('Prompts to try')}
                  </p>
                  <ul className='mt-4 space-y-3'>
                    {prompts.map((prompt, index) => (
                      <li
                        key={prompt}
                        className={styles.rv}
                        style={delay(index * 90)}
                      >
                        <button
                          type='button'
                          onClick={() =>
                            fillIdea(
                              t(prompt),
                              /\b(instrumental|no vocals)\b/i.test(prompt)
                                ? true
                                : undefined,
                            )
                          }
                          className='group flex w-full items-start gap-4 rounded-2xl bg-white p-5 text-start shadow-[0_18px_44px_-30px_rgba(40,30,120,0.45)] ring-1 ring-[#0f0f28]/[0.06] transition hover:ring-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-[#14141f] dark:ring-white/10 dark:hover:ring-primary-400/50'
                        >
                          <span
                            className={clsxm(
                              'min-w-0 flex-1 text-[16px] leading-7',
                              tone.ink,
                            )}
                          >
                            “{t(prompt)}”
                          </span>
                          <span className='mt-0.5 inline-flex flex-none items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-600 transition-colors group-hover:text-primary-700 dark:text-primary-400'>
                            {t('Use this')}
                            <ArrowGlyph className='h-3.5 w-3.5' />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </Stage>
      </div>
    </section>
  );
}
